import React, { useState, useEffect, useContext } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Filter, X, ChevronLeft, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import {
  EditModeProvider,
  EditModeContext,
  AdminToolbar,
  EditableText
} from '../components/EditableContent';
import CMSBlockRenderer from '../components/CMSBlockRenderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductsPage() {
  return (
    <EditModeProvider pageSlug="products">
      <ProductsPageContent />
    </EditModeProvider>
  );
}

function ProductsPageContent() {
  const { slug: categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    category: categorySlug || '',
    brand: searchParams.get('brand') || '',
    inStock: searchParams.get('inStock') === 'true',
    minPrice: searchParams.get('minPrice') || '',
    maxPrice: searchParams.get('maxPrice') || '',
  });

  useEffect(() => {
    fetchCategories();
    fetchBrands();
  }, []);

  useEffect(() => {
    setFilters(prev => ({ ...prev, category: categorySlug || '' }));
  }, [categorySlug]);

  useEffect(() => {
    fetchProducts();
  }, [filters, searchParams]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setCategories(res.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchBrands = async () => {
    try {
      const res = await axios.get(`${API}/products/brands/list`);
      setBrands(res.data);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.category) params.set('category', filters.category);
      if (filters.brand) params.set('brand', filters.brand);
      if (filters.inStock) params.set('in_stock', 'true');
      if (filters.minPrice) params.set('min_price', filters.minPrice);
      if (filters.maxPrice) params.set('max_price', filters.maxPrice);
      params.set('page', searchParams.get('page') || '1');

      const res = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(res.data.products);
      setPagination({
        page: res.data.page,
        pages: res.data.pages,
        total: res.data.total,
      });
    } catch (err) {
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setSearchParams(prev => {
      if (value) {
        prev.set(key, value);
      } else {
        prev.delete(key);
      }
      prev.delete('page');
      return prev;
    });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: categorySlug || '',
      brand: '',
      inStock: false,
      minPrice: '',
      maxPrice: '',
    });
    setSearchParams({});
  };

  const handlePageChange = (newPage) => {
    setSearchParams(prev => {
      prev.set('page', newPage.toString());
      return prev;
    });
  };

  const currentCategory = categories.find(c => c.slug === filters.category);

  // SEO meta for the page
  const pageTitle = currentCategory ? currentCategory.name : 'All Products';
  const pageDescription = currentCategory 
    ? `Browse ${currentCategory.name} products from Tech Distribution Australia. Industrial-grade equipment with trade pricing.`
    : 'Browse our complete range of industrial technology products. Networking, solar controllers, power systems, and more.';

  return (
    <Layout>
      <SEO 
        title={pageTitle}
        description={pageDescription}
        url={currentCategory ? `/category/${currentCategory.slug}` : '/products'}
        keywords={`${pageTitle}, industrial technology, Australia, networking, trade pricing`}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="products-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-black text-foreground uppercase tracking-tight">
              {currentCategory ? currentCategory.name : 'All Products'}
            </h1>
            <p className="text-muted-foreground mt-1">
              {pagination.total} products found
            </p>
          </div>
          <Button
            variant="outline"
            className="md:hidden border-zinc-700 text-foreground"
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
        </div>

        <div className="flex gap-8">
          {/* Sidebar Filters */}
          <aside className={`${filtersOpen ? 'fixed inset-0 z-50 bg-background p-6' : 'hidden'} md:block md:relative md:w-64 md:flex-shrink-0`}>
            <div className="md:sticky md:top-24 space-y-6">
              <div className="flex items-center justify-between md:hidden">
                <h2 className="text-lg font-bold">Filters</h2>
                <Button variant="ghost" size="icon" onClick={() => setFiltersOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>

              {/* Search */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">Search</label>
                <Input
                  placeholder="Search products..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="bg-card border-border"
                  data-testid="search-filter"
                />
              </div>

              {/* Category */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">Category</label>
                <Select value={filters.category || "all"} onValueChange={(v) => handleFilterChange('category', v === 'all' ? '' : v)}>
                  <SelectTrigger className="bg-card border-border" data-testid="category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.slug}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Brand */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">Brand</label>
                <Select value={filters.brand || "all"} onValueChange={(v) => handleFilterChange('brand', v === 'all' ? '' : v)}>
                  <SelectTrigger className="bg-card border-border" data-testid="brand-filter">
                    <SelectValue placeholder="All Brands" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Brands</SelectItem>
                    {brands.map(brand => (
                      <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Price Range */}
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">Price Range</label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    placeholder="Min"
                    value={filters.minPrice}
                    onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                    className="bg-card border-border"
                    data-testid="min-price-filter"
                  />
                  <Input
                    type="number"
                    placeholder="Max"
                    value={filters.maxPrice}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                    className="bg-card border-border"
                    data-testid="max-price-filter"
                  />
                </div>
              </div>

              {/* In Stock */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="inStock"
                  checked={filters.inStock}
                  onCheckedChange={(checked) => handleFilterChange('inStock', checked)}
                  data-testid="stock-filter"
                />
                <label htmlFor="inStock" className="text-sm text-foreground cursor-pointer">
                  In Stock Only
                </label>
              </div>

              {/* Clear Filters */}
              <Button
                variant="outline"
                className="w-full border-zinc-700"
                onClick={clearFilters}
                data-testid="clear-filters"
              >
                Clear Filters
              </Button>
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="bg-card border border-border rounded-sm animate-pulse">
                    <div className="aspect-square bg-zinc-800" />
                    <div className="p-4 space-y-3">
                      <div className="h-4 bg-zinc-800 rounded w-1/4" />
                      <div className="h-5 bg-zinc-800 rounded w-3/4" />
                      <div className="h-6 bg-zinc-800 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg">No products found</p>
                <Button variant="outline" className="mt-4" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
                  {products.map(product => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8" data-testid="pagination">
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={pagination.page <= 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-muted-foreground px-4">
                      Page {pagination.page} of {pagination.pages}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      disabled={pagination.page >= pagination.pages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <CMSBlockRenderer excludeTypes={[]} />
      <AdminToolbar />
    </Layout>
  );
}
